import React from "react";
import { Box, Paper, Typography, Skeleton, Stack } from "@mui/material";

const CRITICAL_COLOR = "#e57373";
const HIGH_COLOR = "#FEA51D";
const IP_COLOR = "#81d4fa";
const PANEL_COLOR = "#a5b4fc";

const Val = ({ color = "#eaeaea", children }) => (
  <Box component="span" sx={{ color, fontWeight: 700 }}>
    {children}
  </Box>
);

const Mono = ({ children }) => (
  <Box component="span" sx={{ color: IP_COLOR, fontFamily: "monospace", fontSize: "0.95em" }}>
    {children}
  </Box>
);

const Section = ({ title, last, children }) => (
  <Box sx={{ mb: last ? 0 : 2 }}>
    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#BBB", mb: 0.75, textTransform: "uppercase", letterSpacing: 0.8 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const Para = ({ children }) => (
  <Typography sx={{ fontSize: 13.5, lineHeight: 1.65, color: "#DDD" }}>
    {children}
  </Typography>
);

const Bullet = ({ children }) => (
  <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
    <Box component="span" sx={{ color: "#555", flexShrink: 0, lineHeight: 1.6 }}>•</Box>
    <Typography sx={{ fontSize: 13.5, lineHeight: 1.6, color: "#999" }}>{children}</Typography>
  </Box>
);

const ExplainPanel = ({
  loading,
  products,
  cardsSummary,
  severitySummary,
  internalPriority,
  externalPriority,
  topInt,
  topExt,
  sampleCriticalOrHighDetails,
  fmtPriorityList,
  countByType = {},
}) => {
  const hasAgentTypes = products?.agent && Object.keys(countByType).length > 0;
  const hasPriority = internalPriority.length > 0 || externalPriority.length > 0;
  const hasMostActive = products?.lecs && (topInt || topExt);
  const hasSample = sampleCriticalOrHighDetails.length > 0;

  return (
    <Paper
      sx={{ mb: 1.5, p: 2.5, backgroundColor: "#1e1e1e", borderRadius: "15px" }}
      elevation={2}
    >
      {loading ? (
        <Stack spacing={1} sx={{ py: 1 }}>
          <Skeleton variant="text" width="80%" sx={{ bgcolor: "rgba(255,255,255,0.07)", fontSize: 14 }} />
          <Skeleton variant="text" width="65%" sx={{ bgcolor: "rgba(255,255,255,0.07)", fontSize: 14 }} />
          <Skeleton variant="text" width="72%" sx={{ bgcolor: "rgba(255,255,255,0.07)", fontSize: 14 }} />
          <Skeleton variant="text" width="50%" sx={{ bgcolor: "rgba(255,255,255,0.07)", fontSize: 14 }} />
        </Stack>
      ) : (
        <Box>
          {/* Overview */}
          <Section title="Overview — last 24h" >
            <Para>
              The probes reported{" "}
              {products?.lecs && <><Val color={CRITICAL_COLOR}>{cardsSummary.critical.toLocaleString()}</Val> critical </>}
              {products?.lecs && products?.agent && " · "}
              {products?.agent && <><Val color={CRITICAL_COLOR}>{cardsSummary.criticalAgent.toLocaleString()}</Val> critical from AGENTs</>}
              {" "}and{" "}
              {products?.lecs && <><Val color={HIGH_COLOR}>{cardsSummary.high.toLocaleString()}</Val> high </>}
              {products?.lecs && products?.agent && " · "}
              {products?.agent && <><Val color={HIGH_COLOR}>{cardsSummary.highAgent.toLocaleString()}</Val> high from AGENTs</>}
              {" "}events.
              {products?.lecs && (
                <>{" "}These involved <Val>{severitySummary.internalHosts.toLocaleString()}</Val> internal
                {" "}and <Val>{severitySummary.externalHosts.toLocaleString()}</Val> external hosts.</>
              )}
            </Para>
          </Section>

          {/* Hosts to prioritise */}
          {hasPriority && (
            <Section title="Hosts to prioritise (critical / CVE)">
              {internalPriority.length > 0 && (
                <Bullet>Internal: <Mono>{fmtPriorityList(internalPriority)}</Mono></Bullet>
              )}
              {externalPriority.length > 0 && (
                <Bullet>External: <Mono>{fmtPriorityList(externalPriority)}</Mono></Bullet>
              )}
            </Section>
          )}

          {/* Most active hosts */}
          {hasMostActive && (
            <Section title="Most active hosts">
              {topInt && <Bullet>Internal: <Mono>{topInt}</Mono></Bullet>}
              {topExt && <Bullet>External: <Mono>{topExt}</Mono></Bullet>}
            </Section>
          )}

          {/* System events by type */}
          {hasAgentTypes && (
            <Section title="Event types — last 7 days">
              <Para>
                {Object.entries(countByType)
                  .sort((a, b) => b[1] - a[1])
                  .map(([type, count], i, arr) => (
                    <Box key={type} component="span">
                      <Box component="span" sx={{ color: PANEL_COLOR }}>{type}</Box>{" "}
                      <Val>{count.toLocaleString()}</Val>
                      {i < arr.length - 1 ? ",  " : "."}
                    </Box>
                  ))}
              </Para>
            </Section>
          )}

          {/* Example event */}
          {hasSample && (
            <Section title="Example event">
              <Para>
                &quot;{sampleCriticalOrHighDetails[0].dettaglio.length > 80
                  ? `${sampleCriticalOrHighDetails[0].dettaglio.slice(0, 77)}…`
                  : sampleCriticalOrHighDetails[0].dettaglio}&quot;
                {" "}— from <Mono>{sampleCriticalOrHighDetails[0].ipSource}</Mono> to{" "}
                <Mono>{sampleCriticalOrHighDetails[0].ipDestination}</Mono>.
                {" "}Click the Critical or High cards below to see the full list filtered to the last 24 hours.
              </Para>
            </Section>
          )}

          {/* What to do next */}
          <Section title="What to do next">
            <Para>
              Click the <Val color={CRITICAL_COLOR}>Critical</Val> or <Val color={HIGH_COLOR}>High</Val> cards
              above to open the event list filtered to the last 24 hours — there you will find full IP addresses,
              event details, and severity for each finding.
            </Para>
          </Section>

          {/* Intelligence tab */}
          <Section title="Intelligence tab" last>
            <Bullet>
              <Box component="span" sx={{ color: PANEL_COLOR }}>Map</Box> / <Box component="span" sx={{ color: PANEL_COLOR }}>Count by country</Box>
              {" "}— geographic origin of external events
            </Bullet>
            <Bullet>
              <Box component="span" sx={{ color: PANEL_COLOR }}>IPs detected</Box>
              {" "}— most active internal and external IPs from intelligence data
            </Bullet>
            <Bullet>
              <Box component="span" sx={{ color: PANEL_COLOR }}>DNS requests</Box>
              {" "}— traffic by hour, record type (A, HTTPS, AAAA), and domain
            </Bullet>
          </Section>
        </Box>
      )}
    </Paper>
  );
};

export default ExplainPanel;
